import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function LeicesterCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Leicester Pothole Claims | 6,432 Repairs in 2023/24 | Fixtyer"
        description="Leicester City Council is AMBER on all four DfT scorecards. U-road RED condition jumped from 17% to 24%, pothole repairs peaked at 6,432 in 2023/24, and 80% of the network is unclassified. See what this means for your claim."
        canonical="/councils/leicester"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Leicester', url: '/councils/leicester' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Leicester is AMBER on all four DfT scorecards — can I still claim?',
            answer: 'Yes. An overall AMBER rating confirms the Department for Transport considers Leicester\'s maintenance adequate but not exemplary — and your claim turns on the specific defect, not the headline badge. Section 58 depends on whether the council had a reasonable system to inspect and repair the road where you were damaged. Leicester\'s own data shows U-road RED condition rising from 17% to 24%, pothole repairs peaking at 6,432 in 2023/24, and a methodology switch from SCANNER to AI surveys in 2024 — all of which are arguable on the facts of your case.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 663.7km — roughly 80% of Leicester\'s 829.9km carriageway network. The council\'s own report states unclassified roads "are not routinely surveyed by SCANNER" and are assessed instead by Coarse Visual Inspection (CVI). At the last published survey, 24% of U-roads were in RED condition, up from a steady 17% in 2020–2022. The 2024 U-road RED figure is blank in the published report.',
          },
          {
            question: 'Does the missing 2024 U-road condition data weaken my claim?',
            answer: 'No — it strengthens the structural argument. Leicester published RED percentages for U-roads every year from 2020 to 2023, then left the 2024 cell empty while simultaneously switching classified-road surveys from SCANNER to AI-based assessment. A council cannot rely on network knowledge it chose not to publish, especially after a methodology change that the report itself flags.',
          },
          {
            question: 'Pothole repairs peaked at 6,432 in 2023/24 — does that mean the roads are fixed now?',
            answer: 'Leicester still filled 5,562 potholes in 2024/25 — roughly 15 every day — and resurfaced only approximately 2.4km of its 829.9km network in that same year. The council\'s five-year total is 25,008 pothole repairs. Reactive patching on a network where a quarter of carriageways are concrete construction is maintenance, not recovery.',
          },
          {
            question: 'Leicester allocates 100% of capital spend to prevention — does that block Section 58?',
            answer: 'Not automatically. Capital prevention and revenue reaction are separate budgets. Leicester reports 0% preventative share on revenue maintenance and 51.7% reactive share in 2024/25. Section 58 turns on whether the specific defect was reasonably inspected and repaired — not on a capital table showing 100% preventative allocation while 6,000+ potholes are filled annually from the revenue budget.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Leicester City Council"
        councilSlug="leicester"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/leicester"
        headline="Leicester Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Leicester</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Leicester: Amber Everywhere, Pressure on U-Roads
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Leicester City Council earns <strong className="text-amber-400">AMBER on all four DfT scorecards</strong> — 
            condition, spend and best practice included. On its own 829.9km network, unclassified-road RED condition 
            jumped from <strong className="text-rose-400">17% to 24%</strong>, pothole repairs peaked at 
            <strong className="text-rose-400"> 6,432 in 2023/24</strong>, and the council resurfaced just 
            2.4km while filling 25,008 potholes in five years.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">663.7km</div>
            <div className="text-white font-medium mt-2">Unclassified roads — 80% of the network</div>
            <div className="text-brand-300 text-sm mt-1">
              Not routinely surveyed by SCANNER. CVI-only assessment. RED condition rose from 17% to 24% 
              before the 2024 figure was left blank in the published report.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data from Leicester's own transparency report — 
            classified roads recovering, U-roads deteriorating
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (99.8km — 12% of network): improving</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">4.2%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">down from 6.4% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">27.6%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">broadly flat</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">68.2%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 64% in 2020</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Credit where due: main roads have genuinely improved. But A-roads are just one-eighth of the carriageway network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (66.4km — 8% of network): volatile, now recovering</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">6.15%</td>
                    <td className="px-4 py-2 text-center">36%</td>
                    <td className="px-4 py-2 text-center">57.85%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">6.85%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">42.9%</td>
                    <td className="px-4 py-2 text-center">50.25%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">9%</td>
                    <td className="px-4 py-2 text-center">39.05%</td>
                    <td className="px-4 py-2 text-center">51.95%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">4.05%</td>
                    <td className="px-4 py-2 text-center">29.55%</td>
                    <td className="px-4 py-2 text-center">66.4%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">4.25%</td>
                    <td className="px-4 py-2 text-center font-bold">28%</td>
                    <td className="px-4 py-2 text-center font-bold">67.75%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B/C roads hit 9% RED and 42.9% amber in 2021–2022 before recovering — but at the 2022 peak, 
              roughly one in ten classified local roads needed maintenance intervention.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Capital Prevention, Revenue Reaction</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£5.4m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£6.1m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">51.7%</div>
                <div className="text-brand-300 text-sm">Revenue reactive share 2024/25</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Leicester reports <span className="text-emerald-400">100% preventative capital spend</span> — 
              yet the DfT still rates spend <span className="text-amber-400">AMBER</span>, revenue maintenance 
              is overwhelmingly reactive, and the council filled 25,008 potholes in five years. Prevention on paper; 
              patching in practice.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Blind Spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 663.7km CVI-Only Network
          </h2>
          <p className="text-surface-600 mb-8">
            80% of Leicester's carriageway is unclassified — not routinely surveyed by SCANNER
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">24%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-medium text-surface-400 italic">Not published</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The table below presents results from Coarse Visual Inspection survey (CVI) carried out on unclassified roads (which are not routinely surveyed by SCANNER)."
              source="Leicester City Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Before 2024, these were completed as Surface Condition Assessment for the National Network of Roads (SCANNER). We are now trialling automated road condition assessments, which utilises artificial intelligence."
              source="Leicester City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 17% → 24% Jump</h3>
              <p className="text-surface-600 text-sm mb-3">
                For three consecutive years, exactly 17% of Leicester's U-roads were in RED condition — 
                then the figure jumped to 24% in 2023. That is a 41% relative increase on the road type 
                that makes up four-fifths of the city's carriageway.
              </p>
              <p className="text-surface-600 text-sm">
                At 24% RED on 663.7km, roughly 159km of residential and neighbourhood streets were flagged 
                for maintenance intervention at the last published survey.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Concrete Quarter</h3>
              <p className="text-amber-800 text-sm">
                Leicester's own report notes that approximately one-quarter of the road network is of 
                concrete construction — harder to maintain and more prone to defect formation at joints 
                and edges.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Approximately one-quarter of the city's network is understood to be of concrete road construction (either concrete or overlain concrete)."
              source="Leicester City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Leicester must show it had a reasonable system for 
              knowing the condition of its roads. For the city's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road assessed by SCANNER or only by Coarse Visual Inspection?</li>
              <li>• When U-road RED rose from 17% to 24%, what was done about your street?</li>
              <li>• Why is the 2024 U-road RED figure blank in the published transparency report?</li>
              <li>• Does the switch from SCANNER to AI surveys make pre-2024 records comparable?</li>
            </ul>
            <p className="text-white font-medium">
              A council can't claim detailed network knowledge when 80% of its roads aren't routinely 
              SCANNER-surveyed — and then stop publishing the one metric it does collect.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            25,008 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes on a compact 829.9km urban network
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Public carriageway reports</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Resurfacing spend (revenue)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">4,084</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">—</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">4,806</td>
                  <td className="px-6 py-4 text-center">926</td>
                  <td className="px-6 py-4 text-center">£1,843,186</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">4,124</td>
                  <td className="px-6 py-4 text-center">1,248</td>
                  <td className="px-6 py-4 text-center">£1,384,810</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6,432</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,769</td>
                  <td className="px-6 py-4 text-center">£1,685,894</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">5,562</td>
                  <td className="px-6 py-4 text-center">1,347</td>
                  <td className="px-6 py-4 text-center">£1,882,320</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">25,008</td>
                  <td className="px-6 py-4 text-center font-bold">5,290*</td>
                  <td className="px-6 py-4 text-center font-bold">£6,796,210*</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2.4km Resurfaced, 829.9km Network</h3>
              <p className="text-surface-600 text-sm">
                Leicester resurfaced approximately 2.4km (48,000m²) of carriageway in 2024/25 — 
                less than 0.3% of its road network by length. Meanwhile it filled 5,562 potholes 
                from the revenue budget. That ratio tells you where maintenance effort actually goes.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Permanent-Fix Policy</h3>
              <p className="text-amber-800 text-sm">
                Leicester states a policy of first-time permanent fixes — but the pothole numbers 
                keep climbing. Peak year 2023/24 saw 57% more repairs than 2020/21.
              </p>
            </div>
          </div>
          <div className="mt-6">
            <QuoteCard
              quote="The city council policy is to complete a first time permanent fix when carrying out pothole repairs, using asphalt in accordance with the HMEP Potholes review - prevention and a better cure."
              source="Leicester City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>
          <p className="text-surface-500 text-xs mt-4 italic">
            *Public report and revenue spend totals cover 2021/22–2024/25 only; 2020/21 pothole count published separately.
          </p>
        </div>
      </section>

      {/* The Asset Management Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Surveys, Systems and Outstanding Plans
          </h2>
          <p className="text-surface-600 mb-8">
            What Leicester says about how it inspects and prioritises its network
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote={'We carry out road condition surveys each financial year to identify existing condition of the carriageway. From the condition reports, we can identify the areas that need intervention (i.e. "red" scores). We also have our internal highway management team that carry out highway safety inspections and highlight any areas of the road network which require larger maintenance schemes. These are then prioritised based on the condition, road hierarchy, number of complaints, location, bus routes etc.'}
              source="Leicester City Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Please note that this document is due to be reviewed and extended, until updated at a suitable time."
              source="Leicester City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">New Tools, Old Gaps</h3>
              <p className="text-surface-600 text-sm mb-3">
                Leicester has introduced the Alloy asset management platform and is trialling AI-based 
                condition surveys — but its Local Transport and Transport Asset Management plans are 
                explicitly overdue for review.
              </p>
              <p className="text-surface-600 text-sm">
                Modern systems don't automatically cure a 24% U-road RED rate or a blank 2024 condition cell.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2025/26 Capital Programme</h3>
              <p className="text-amber-800 text-sm mb-3">
                £6.1 million is allocated for 2025/26, with £2.202m (36.1%) for carriageway maintenance 
                schemes across 13 named roads including London Road, King Richards Road and Vulcan Road.
              </p>
              <p className="text-amber-800 text-sm">
                Named schemes help if your defect is on a listed route. If it isn't, the prioritisation 
                criteria — condition, hierarchy, complaints, bus routes — are your cross-examination points.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER everywhere means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an All-Amber Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Leicester is not Leicestershire — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition genuinely improving — RED down from 6.4% to 4.2%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 100% capital spend classed as preventative
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Projected capital spend (£6.1m) exceeds DfT allocation (£5.4m) in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> First-time permanent fix policy; Alloy asset management system
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual condition surveys and documented Code of Practice compliance
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on A-roads and named capital scheme routes. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER on all four scorecards — no green badges anywhere
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road RED up from 17% to 24%; 2024 figure not published
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 80% of network not routinely SCANNER-surveyed — CVI only
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 25,008 pothole repairs in five years on 829.9km — defects outpace resurfacing
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> ~25% concrete construction — elevated defect risk at joints
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Asset management plans overdue for review per the council's own admission
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with documented surveys and a permanent-fix policy, your claim lives or dies on 
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching, concrete joints)</li>
              <li>• Road class — on a U-road, the CVI-only survey gap and 24% RED rate are your strongest structural arguments</li>
              <li>• Whether your street was on the 2025/26 named resurfacing list — if not, ask why not</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Leicester's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Leicester?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            An all-amber council still owes you a reasonable inspection regime. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road CVI survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No concrete-network analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road RED rise (17% → 24%) documented</li>
                <li>✅ CVI-only survey gap argued</li>
                <li>✅ 25,008 repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Leicester</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=leicester"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Leicester Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Leicester is AMBER on all four DfT scorecards — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. An overall AMBER rating confirms the Department for Transport considers Leicester's 
                maintenance adequate but not exemplary — and your claim turns on the specific defect, not 
                the headline badge. Section 58 depends on whether the council had a reasonable system to 
                inspect and repair the road where you were damaged. Leicester's own data shows U-road RED 
                condition rising from 17% to 24%, pothole repairs peaking at 6,432 in 2023/24, and a 
                methodology switch from SCANNER to AI surveys in 2024 — all of which are arguable on the 
                facts of your case.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 663.7km — roughly 80% of Leicester's 829.9km carriageway network. The 
                council's own report states unclassified roads "are not routinely surveyed by SCANNER" and 
                are assessed instead by Coarse Visual Inspection (CVI). At the last published survey, 24% 
                of U-roads were in RED condition, up from a steady 17% in 2020–2022. The 2024 U-road RED 
                figure is blank in the published report.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the missing 2024 U-road condition data weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No — it strengthens the structural argument. Leicester published RED percentages for U-roads 
                every year from 2020 to 2023, then left the 2024 cell empty while simultaneously switching 
                classified-road surveys from SCANNER to AI-based assessment. A council cannot rely on network 
                knowledge it chose not to publish, especially after a methodology change that the report itself flags.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs peaked at 6,432 in 2023/24 — does that mean the roads are fixed now?
              </h3>
              <p className="text-surface-600 text-sm">
                Leicester still filled 5,562 potholes in 2024/25 — roughly 15 every day — and resurfaced only 
                approximately 2.4km of its 829.9km network in that same year. The council's five-year total 
                is 25,008 pothole repairs. Reactive patching on a network where a quarter of carriageways are 
                concrete construction is maintenance, not recovery.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Leicester allocates 100% of capital spend to prevention — does that block Section 58?
              </h3>
              <p className="text-surface-600 text-sm">
                Not automatically. Capital prevention and revenue reaction are separate budgets. Leicester 
                reports 0% preventative share on revenue maintenance and 51.7% reactive share in 2024/25. 
                Section 58 turns on whether the specific defect was reasonably inspected and repaired — not 
                on a capital table showing 100% preventative allocation while 6,000+ potholes are filled 
                annually from the revenue budget.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.leicester.gov.uk/highways-maintenance-leicester"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Leicester City Council Local Highways Maintenance Transparency Report (2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
